/**
 * NewsPulseFarmersPulse — "My Farm" profile + personalized farmer feed.
 *
 * Serves as the FarmersPulse.com Phase 1 reference implementation inside
 * NewsPulse. Auth is required only for save/read of the profile — the
 * public feed works anonymously. Uses the SocialPulse Firebase Google
 * auth already wired into `AuthContext` (token in localStorage).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sprout, MapPin, Save, LogIn, Wheat, LineChart, Bell, BellOff, Trash2,
  Cloud, CloudRain, Sun, CloudSun, CloudSnow, Wind, Droplets, IndianRupee, DollarSign,
  BadgeInfo,
} from "lucide-react";
import { npApi, hasNpAuthToken } from "./api";
import EventCard from "./EventCard";
import PublisherWidget from "../../components/publisher/PublisherWidget";
import { useAuth } from "@/contexts/AuthContext";

// Stable reference for the fallback "session" user so React's dep array
// on our fetch effect below doesn't re-fire every render.
const _SESSION_USER = Object.freeze({ id: "session" });

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? "bg-emerald-600 text-white border border-emerald-600"
          : "bg-white text-slate-700 border border-slate-200 hover:border-emerald-400 hover:text-emerald-700"
      }`}
    >
      {children}
    </button>
  );
}

/** Map Open-Meteo WMO weather codes → Lucide icon + label. */
function weatherIcon(code) {
  if (code == null) return { Icon: Cloud, label: "—" };
  if (code === 0) return { Icon: Sun, label: "Clear" };
  if (code <= 2) return { Icon: CloudSun, label: "Mainly clear" };
  if (code === 3) return { Icon: Cloud, label: "Overcast" };
  if (code >= 45 && code <= 48) return { Icon: Cloud, label: "Fog" };
  if (code >= 51 && code <= 67) return { Icon: CloudRain, label: "Rain" };
  if (code >= 71 && code <= 77) return { Icon: CloudSnow, label: "Snow" };
  if (code >= 80 && code <= 82) return { Icon: CloudRain, label: "Showers" };
  if (code >= 95) return { Icon: CloudRain, label: "Thunderstorm" };
  return { Icon: Cloud, label: "Weather" };
}

function WeatherCard({ weather, err }) {
  if (err && !weather) {
    // Graceful — most likely "no_region_saved" or "geocode_miss"
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="farmerspulse-weather-empty">
        <div className="flex items-center gap-2 mb-1">
          <Cloud className="w-5 h-5 text-sky-600" />
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700">Local weather</h3>
        </div>
        <p className="text-xs text-slate-500">
          Add a nearest city / town to your profile to see a 7-day forecast for your farm.
        </p>
      </section>
    );
  }
  if (!weather) return null;
  const cur = weather.forecast?.current || {};
  const daily = weather.forecast?.daily || [];
  const { Icon: CurIcon, label: curLabel } = weatherIcon(cur.code);
  return (
    <section className="rounded-xl border border-slate-200 bg-gradient-to-br from-sky-50 to-blue-50 p-5" data-testid="farmerspulse-weather">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 inline-flex items-center gap-2">
            <Cloud className="w-4 h-4 text-sky-600" /> Local weather
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {weather.location.name}{weather.location.admin1 ? `, ${weather.location.admin1}` : ""} · via Open-Meteo
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-400">
          Now
        </span>
      </div>
      <div className="flex items-center gap-4 mb-4">
        <CurIcon className="w-12 h-12 text-sky-700" strokeWidth={1.4} />
        <div>
          <div className="text-3xl font-bold text-slate-900" data-testid="farmerspulse-weather-temp">
            {cur.temp_c != null ? `${Math.round(cur.temp_c)}°C` : "—"}
          </div>
          <div className="text-xs text-slate-600">{curLabel}</div>
        </div>
        <div className="ml-auto grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1"><Droplets className="w-3.5 h-3.5" /> {cur.humidity ?? "—"}%</span>
          <span className="inline-flex items-center gap-1"><Wind className="w-3.5 h-3.5" /> {cur.wind_kmh != null ? `${Math.round(cur.wind_kmh)} km/h` : "—"}</span>
          <span className="inline-flex items-center gap-1"><CloudRain className="w-3.5 h-3.5" /> {cur.precip_mm ?? 0} mm</span>
        </div>
      </div>
      {/* 7-day strip */}
      <div className="grid grid-cols-7 gap-1.5 text-center" data-testid="farmerspulse-weather-forecast">
        {daily.slice(0, 7).map((d) => {
          const { Icon } = weatherIcon(d.code);
          const day = new Date(d.date).toLocaleDateString("en", { weekday: "short" });
          return (
            <div key={d.date} className="rounded-lg bg-white/80 p-2">
              <div className="text-[10px] font-semibold text-slate-500 uppercase">{day}</div>
              <Icon className="w-5 h-5 mx-auto text-sky-700 my-1" strokeWidth={1.4} />
              <div className="text-[11px] text-slate-800 font-semibold">
                {d.tmax_c != null ? Math.round(d.tmax_c) : "—"}°
              </div>
              <div className="text-[10px] text-slate-500">
                {d.tmin_c != null ? Math.round(d.tmin_c) : "—"}°
              </div>
              {d.precip_chance != null && d.precip_chance >= 20 && (
                <div className="text-[9px] text-blue-600 mt-0.5">
                  {d.precip_chance}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PricesCard({ prices }) {
  if (!prices || prices.length === 0) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="farmerspulse-prices">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-700 inline-flex items-center gap-2">
            <LineChart className="w-4 h-4 text-amber-600" /> Today's mandi prices
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            For your saved crops & commodities
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5"
          title="Not market quotes — indicative reference data only."
        >
          <BadgeInfo className="w-3 h-3" /> Sample data
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {prices.map((p) => {
          const isINR = p.currency === "INR";
          const CurrencyIcon = isINR ? IndianRupee : DollarSign;
          return (
            <div
              key={p.id}
              className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2"
              data-testid={`farmerspulse-price-${p.id}`}
            >
              <span className="rounded-md bg-white border border-slate-200 p-1.5 shrink-0">
                <CurrencyIcon className="w-4 h-4 text-amber-700" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">{p.label}</div>
                <div className="text-[11px] text-slate-500">per {p.unit} · {p.country}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-slate-900">
                  {p.currency === "INR" ? "₹" : p.currency === "USD" ? "$" : ""}
                  {p.price.toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CatalogPicker({ label, items, selected, onToggle, testIdPrefix }) {
  // group items by their "group" field
  const groups = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const g = it.group || "other";
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(it);
    }
    return Array.from(m.entries());
  }, [items]);
  return (
    <section>
      <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
        {label} <span className="text-slate-400">({selected.length})</span>
      </h3>
      <div className="space-y-3">
        {groups.map(([g, list]) => (
          <div key={g}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">{g}</div>
            <div className="flex flex-wrap gap-2">
              {list.map((it) => (
                <Chip
                  key={it.id}
                  active={selected.includes(it.id)}
                  onClick={() => onToggle(it.id)}
                  testId={`${testIdPrefix}-${it.id}`}
                >
                  {it.label}
                </Chip>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function NewsPulseFarmersPulse() {
  const auth = useAuth() || {};
  const { signInWithGoogleProvider } = auth;
  // Consider the caller "signed in" if AuthContext has a user OR a JWT
  // exists in localStorage. Keep the derived reference stable so the
  // fetch effect below doesn't infinite-loop.
  const hasStoredToken = hasNpAuthToken();
  const user = auth.user || (hasStoredToken ? _SESSION_USER : null);

  const [crops, setCrops] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [profile, setProfile] = useState(null);
  const [feed, setFeed] = useState([]);
  const [weather, setWeather] = useState(null);
  const [weatherErr, setWeatherErr] = useState("");
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Form state.
  const [farmName, setFarmName] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [size, setSize] = useState("");
  const [selCrops, setSelCrops] = useState([]);
  const [selComm, setSelComm] = useState([]);
  const [alerts, setAlerts] = useState(true);

  // Load catalogs + profile + feed.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [cRes, mRes] = await Promise.all([
          npApi.fpCropsCatalog(),
          npApi.fpCommoditiesCatalog(),
        ]);
        if (cancelled) return;
        setCrops(cRes.crops || []);
        setCommodities(mRes.commodities || []);
        if (user) {
          const p = await npApi.fpGetMyFarm().catch(() => null);
          if (p) {
            setProfile(p);
            setFarmName(p.farm_name || "");
            setCountry(p.country || "");
            setState(p.state || "");
            setDistrict(p.district || "");
            setSize(p.farm_size_acres != null ? String(p.farm_size_acres) : "");
            setSelCrops(p.crops || []);
            setSelComm(p.commodities || []);
            setAlerts(p.alerts_enabled !== false);
            // Fire-and-forget weather + prices — they may 404/502 and that's OK
            npApi.fpMyWeather()
              .then((w) => { if (!cancelled) setWeather(w); })
              .catch((e) => { if (!cancelled) setWeatherErr(String(e?.message || "")); });
            npApi.fpMyPrices()
              .then((r) => { if (!cancelled) setPrices(r?.prices || []); })
              .catch(() => {});
          }
        }
        const f = await npApi.fpFeed({ limit: 30 }).catch(() => []);
        if (!cancelled) setFeed(f || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const toggle = (setter, list, id) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const body = {
        farm_name: farmName.trim() || null,
        country: country.trim() || null,
        state: state.trim() || null,
        district: district.trim() || null,
        farm_size_acres: size ? Number(size) : null,
        crops: selCrops,
        commodities: selComm,
        alerts_enabled: alerts,
      };
      const saved = await npApi.fpPutMyFarm(body);
      setProfile(saved);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      // Re-fetch personalized feed, weather + prices so they pick up new tokens.
      const [f, w, p] = await Promise.all([
        npApi.fpFeed({ limit: 30 }).catch(() => []),
        npApi.fpMyWeather().catch((e) => { setWeatherErr(String(e?.message || "")); return null; }),
        npApi.fpMyPrices().catch(() => ({ prices: [] })),
      ]);
      setFeed(f || []);
      setWeather(w);
      if (w) setWeatherErr("");
      setPrices((p && p.prices) || []);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!user) return;
    if (!window.confirm("Remove your farm profile? Your feed will reset to the general agriculture stream.")) return;
    await npApi.fpDeleteMyFarm();
    setProfile(null);
    setFarmName(""); setCountry(""); setState(""); setDistrict("");
    setSize(""); setSelCrops([]); setSelComm([]); setAlerts(true);
    const f = await npApi.fpFeed({ limit: 30 }).catch(() => []);
    setFeed(f || []);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8" data-testid="farmerspulse">
      {/* Hero */}
      <section className="rounded-2xl bg-gradient-to-br from-emerald-900 via-green-800 to-lime-700 p-8 md:p-12 text-white mb-8 relative overflow-hidden">
        <div className="absolute -right-8 -bottom-14 opacity-10">
          <Sprout className="w-72 h-72" strokeWidth={0.6} />
        </div>
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.2em] text-lime-200 mb-2">
            FarmersPulse.com · Personalized Farmer Intelligence
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
            Your farm. Your crops. Your news.
          </h1>
          <p className="mt-3 text-lime-100 max-w-2xl">
            Pick the crops and commodities you grow, and we'll narrow the global
            agriculture stream to what actually affects you — pest alerts, mandi
            prices, weather, and farm policy.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: profile form */}
        <div className="lg:col-span-2 space-y-6" data-testid="farmerspulse-profile">
          <div className="pb-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
              <Wheat className="w-5 h-5 text-emerald-600" /> My Farm
            </h2>
            {profile && (
              <button
                type="button"
                onClick={onDelete}
                className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                data-testid="farmerspulse-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Reset
              </button>
            )}
          </div>

          {!user ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="farmerspulse-signin-prompt">
              <p className="text-slate-800 font-semibold mb-1">Sign in to build your farm profile</p>
              <p className="text-sm text-slate-600 mb-4">
                We use your Google account (via Emergent-managed sign-in) to save your
                crops, commodities and region so alerts are personalized to your farm.
              </p>
              <button
                type="button"
                onClick={() => signInWithGoogleProvider && signInWithGoogleProvider()}
                disabled={!signInWithGoogleProvider}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors disabled:opacity-50"
                data-testid="farmerspulse-signin-btn"
              >
                <LogIn className="w-4 h-4" /> Sign in with Google
              </button>
            </div>
          ) : (
            <form onSubmit={onSave} className="space-y-6">
              {/* Basic details */}
              <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest text-slate-500">Farm name (optional)</span>
                  <input
                    type="text"
                    value={farmName}
                    onChange={(e) => setFarmName(e.target.value)}
                    placeholder="e.g. Green Fields Estate"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    data-testid="farmerspulse-farm-name"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest text-slate-500">Size (acres)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    placeholder="e.g. 2.5"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    data-testid="farmerspulse-farm-size"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest text-slate-500">Country (ISO-2)</span>
                  <input
                    type="text"
                    maxLength={2}
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    placeholder="IN, US, KE…"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase outline-none focus:border-emerald-500"
                    data-testid="farmerspulse-country"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-widest text-slate-500">State / Region</span>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="e.g. Kerala"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    data-testid="farmerspulse-state"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[11px] uppercase tracking-widest text-slate-500">Nearest city / town</span>
                  <input
                    type="text"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    placeholder="e.g. Thiruvananthapuram, Nashik, Iowa City"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                    data-testid="farmerspulse-district"
                  />
                  <span className="mt-1 text-[10px] text-slate-400 block">
                    Used to fetch local weather. Enter a well-known town — small villages may not be found.
                  </span>
                </label>
              </section>

              {/* Crops */}
              <CatalogPicker
                label="Crops I grow"
                items={crops}
                selected={selCrops}
                onToggle={(id) => toggle(setSelCrops, selCrops, id)}
                testIdPrefix="farmerspulse-crop"
              />

              {/* Commodities */}
              <CatalogPicker
                label="Commodities I care about"
                items={commodities}
                selected={selComm}
                onToggle={(id) => toggle(setSelComm, selComm, id)}
                testIdPrefix="farmerspulse-commodity"
              />

              {/* Alerts */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setAlerts((v) => !v)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    alerts
                      ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : "bg-slate-100 text-slate-600 border border-slate-200"
                  }`}
                  data-testid="farmerspulse-alerts-toggle"
                >
                  {alerts ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  Alerts {alerts ? "on" : "off"}
                </button>
                <span className="text-xs text-slate-500">
                  Pest & price alerts for your crops. Delivered inside the app.
                </span>
              </div>

              {/* Submit */}
              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors disabled:opacity-50"
                  data-testid="farmerspulse-save-btn"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "Saving…" : profile ? "Update my farm" : "Save my farm"}
                </button>
                {savedFlash && (
                  <span className="text-xs text-emerald-700 font-semibold" data-testid="farmerspulse-saved-flash">
                    Saved · feed refreshed
                  </span>
                )}
              </div>
            </form>
          )}

          {/* Weather + Prices — only when a profile exists */}
          {user && profile && (
            <>
              <WeatherCard weather={weather} err={weatherErr} />
              <PricesCard prices={prices} />
            </>
          )}
        </div>

        {/* Right: personalized feed */}
        <aside data-testid="farmerspulse-feed">
          <div className="pb-3 mb-4 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 inline-flex items-center gap-2">
              <LineChart className="w-5 h-5 text-emerald-600" /> Your feed
            </h2>
            {profile && (selCrops.length + selComm.length > 0) && (
              <p className="text-xs text-slate-500 mt-1">
                Ranked by relevance to your {selCrops.length + selComm.length} selection{selCrops.length + selComm.length === 1 ? "" : "s"}
              </p>
            )}
          </div>
          {loading ? (
            <p className="text-slate-500 text-sm">Loading feed…</p>
          ) : feed.length ? (
            <div>
              {feed.slice(0, 15).map((e) => <EventCard key={e.event_id} event={e} compact />)}
            </div>
          ) : (
            <p className="text-slate-500 italic text-sm">
              No agriculture stories in the archive yet. Come back once the ingestion pipeline
              picks up its next cycle.
            </p>
          )}
        </aside>
      </div>

      {/* SocialPulse Publisher widget — canonical agriculture stories
          distributed to FarmersPulse. Fail-open when empty. */}
      <PublisherWidget
        siteId="farmerspulse"
        locationId={profile?.state ? `${profile.country || "IN"}-${profile.state}`.toUpperCase() : null}
        categoryId="agriculture"
        title="Deeper reads from SocialPulse Publisher"
        limit={6}
        testIdPrefix="farmerspulse-publisher"
      />

      {/* Footer link back */}
      <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500">
        FarmersPulse is served from the NewsPulse central intelligence graph.{" "}
        <Link to="/about" className="text-emerald-700 hover:underline">Learn how it works →</Link>
      </div>
    </div>
  );
}
